import * as React from 'react';
import { translate, InjectedTranslateProps } from 'react-i18next';
import * as css from './EditTeamDetails.scss';
import { connect, ConnectedProps } from 'react-redux';
import {
  getTeamImageUrl,
  getTeamName,
} from '../../redux/selectors/teamDetails';
import {
  EDIT_MODE_SIZE,
  getImageUrlWithDimensions
} from '../../services/image-helper';
import ImageViewer from '../ImageViewer/ImageViewer';
import { reportUpdateLogoStatus, learnMoreClicked } from '../../redux/actions/bi.actions';
import { logoActionTypes } from '../../consts/bi';
import { State } from '../../redux/configure-store';

import { Text, FormField, Input } from 'wix-style-react';

interface IAppProps extends InjectedTranslateProps, ConnectedProps<typeof connector> {
  onUpdateDetails: Function;
  onUpdateUploadStatus: Function;

  validate?(name: string): void;

  errorMessage?: string;
  isValid?: boolean;
  introClass?: string;
  teamNameClass?: string;
  teamLogoClass?: string;
  delayNameInputFocus?: boolean;

  extractRef?(ref): void;
}

interface IState {
  imageFullUrl: string;
  name: string;
  isNameValid: boolean;
  nameErrorMessage?: string;
  imageUrl: string;
}

class EditTeamDetails extends React.Component<IAppProps, IState> {
  private nameInput;

  constructor(props) {
    super(props);
    const { name, imageUrl, onUpdateDetails } = props;
    const imageFullUrl = this.getFullImageUrl(imageUrl);
    this.state = { name, imageUrl, imageFullUrl, isNameValid: true };
    onUpdateDetails({ name, imageUrl });
  }

  getFullImageUrl(imageUrl) {
    return getImageUrlWithDimensions(imageUrl, EDIT_MODE_SIZE);
  }

  onNameChange(name) {
    this.setState({ name });
    this.reportDetails(name, this.state.imageUrl);
    if (this.props.validate) {
      this.props.validate(name);
    }
  }

  reportDetails(name, imageUrl) {
    this.props.onUpdateDetails({ name, imageUrl });
  }

  onAddImage(imageUrl) {
    const biAction = this.state.imageUrl
      ? logoActionTypes.replace
      : logoActionTypes.add;
    const imageFullUrl = this.getFullImageUrl(imageUrl);
    this.setState({ imageUrl, imageFullUrl });
    this.reportDetails(this.state.name, imageUrl);
    this.props.reportLogoBi(biAction);
  }

  onRemoveImage() {
    this.setState({ imageUrl: null });
    this.reportDetails(this.state.name, null);
    this.props.reportLogoBi(logoActionTypes.remove);
  }

  onUpdateUploadStatus(isLoading) {
    this.props.onUpdateUploadStatus(isLoading);
  }

  componentDidMount() {
    setTimeout(
      () => this.nameInput && this.nameInput.focus(),
      this.props.delayNameInputFocus ? 500 : 0
    );
  }

  render() {
    const { t, introClass, teamNameClass, teamLogoClass, isValid, errorMessage, learnMoreLink, reportLearnMoreBi } = this.props;
    const { imageUrl, name, imageFullUrl } = this.state;
    return (
      <div>
        <div className={`${css.section} ${introClass}`}>
          <Text size="medium" skin="standard" tagName="span" weight="thin">
            {t('teamDetailsModal.intro')}
            <a href={learnMoreLink} target="_blank" data-hook={'learn-more-link'} onClick={reportLearnMoreBi}>
              &nbsp;{t('teamDetailsModal.introLink')}
            </a>
          </Text>
        </div>
        <div className={`${css.section} ${teamNameClass}`}>
          <FormField label={t('teamDetailsModal.teamName')} required>
            <Input
              onChange={e => this.onNameChange(e.target.value)}
              size="large"
              dataHook="team-name-input"
              placeholder={t('teamDetailsModal.inputPlaceholder')}
              value={name || ''}
              status={isValid === false ? Input.StatusError : null}
              statusMessage={
                isValid === false ? t(errorMessage) : ''
              }
              ref={ref => {
                this.nameInput = ref;
                this.props.extractRef && this.props.extractRef(ref);
              }}
            />
          </FormField>
        </div>
        <div className={`${css.section} ${teamLogoClass}`}>
          <FormField
            label={t('teamDetailsModal.teamLogo')}
            infoContent={t('teamDetailsModal.teamLogo.tooltip')}
            infoTooltipProps={{ textAlign: 'start' }}
          >
            <ImageViewer
              imageUrl={imageUrl ? imageFullUrl : ''}
              onAddImage={(_imageUrl) => this.onAddImage(_imageUrl)}
              onRemoveImage={() => this.onRemoveImage()}
              onUpdateUploadStatus={(isLoading: boolean) => this.onUpdateUploadStatus(isLoading)}
            />
          </FormField>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: State, ownProps): {name: string; imageUrl: string; learnMoreLink: string} => {
  return {
    name: getTeamName(state) || ownProps.name,
    imageUrl: getTeamImageUrl(state) || ownProps.imageUrl,
    learnMoreLink: ownProps.learnMoreLink
  };
};

const mapDispatchToProps = dispatch => ({
  reportLogoBi: (actionType: logoActionTypes) => {
    return dispatch(reportUpdateLogoStatus(actionType));
  },
  reportLearnMoreBi: () => {
    return dispatch(learnMoreClicked('/editTeamProfile'));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
export default connector(translate()(EditTeamDetails));
